import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { partial } from 'lodash';
import { PanelLeft, PencilRuler, Users, X } from 'lucide-react';
import { useLayout } from '../../contexts/layout-context';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';

// Icons
import { ReactComponent as NoNameLogo } from '../../assets/images/brand-logo.svg';
import { ReactComponent as SurveyIcon } from '../../assets/icons-redesign/chat-bubble.svg';
import { ReactComponent as Download } from '../../assets/icons-redesign/download.svg';
import { ReactComponent as BarChart } from '../../assets/icons-redesign/bar-chart.svg';
import { isClientMember, isOrgMember } from 'src/services/permission/permission.helpers';
import { isClientUrl, isOrgUrl } from 'src/services/permission/permission.helpers';


const ORG_USER_SIDEBAR_BUTTONS = [
  {
    permissions: [],
    title: 'Surveys',
    subtitle: 'Create, generate, or build surveys using AI or templates',
    color: 'bg-blue-500',
    icon: <SurveyIcon className="size-[18px] fill-white" />,
    path: '/dashboard',
  },
  {
    permissions: [],
    title: 'Reports',
    subtitle: 'Generate insights from ad campaign data',
    color: 'bg-green-501',
    icon: <Download className="size-[16px] mt-[-2px] ml-[-1px] fill-white" />,
    path: '/reports',
  },
  {
    permissions: [],
    title: 'Billing',
    subtitle: 'Generate insights from ad campaign data',
    color: 'bg-yellow-500',
    icon: <Download className="size-[16px] mt-[-2px] ml-[-1px] fill-white" />,
    path: '/billing',
  },
  {
    permissions: [],
    title: 'Performance',
    subtitle: 'Monitor and analyze system performance metrics',
    color: 'bg-orange-500',
    icon: <BarChart className="size-[18px] stroke-white" />,
    path: '/performance',
  },
  {
    permissions: [],
    title: 'Tools',
    subtitle: 'Explore our tools',
    color: 'bg-purple-500',
    icon: <PencilRuler className="size-[18px] fill-white" />,
    path: '/tools',
  },
  {
    permissions: [],
    title: 'Mange Clients',
    subtitle: 'Manage clients and their data',
    color: 'bg-red-502',
    icon: <Users className="size-[18px] fill-white" />,
    path: '/manage-clients',
  },
];

const CLIENT_USER_SIDEBAR_BUTTONS = [
  {
    permissions: [],
    title: 'Reports',
    subtitle: 'Generate insights from ad campaign data',
    color: 'bg-green-1000',
    icon: <Download className="size-[16px] mt-[-2px] ml-[-1px] fill-white" />,
    path: '/client/reports',
  },
  {
    permissions: [],
    title: 'Tools',
    subtitle: 'Explore our tools',
    color: 'bg-purple-500',
    icon: <PencilRuler className="size-[18px] fill-white" />,
    path: '/client/tools',
  },
];

const getSidebarButtons = (userPermissions: any) => {
  // all routes
  if (!userPermissions) {
    return [];
  }


  // all org based routes
  if (isOrgUrl()) {
    if (isOrgMember(userPermissions)) {
      return ORG_USER_SIDEBAR_BUTTONS;
    }
  }

  // all client based routes
  if (isClientUrl()) {
    if (isClientMember(userPermissions) || isOrgMember(userPermissions)) {
      return CLIENT_USER_SIDEBAR_BUTTONS;
    }
  }

  return [];
};


interface AppSidebarButtonProps {
  readonly show?: boolean;
  readonly onClick: () => void;
  readonly title: string;
  readonly subtitle: string;
  readonly icon: React.ReactNode;
  readonly color?: string;
  readonly isActive?: boolean;
}

const AppSidebarButton: React.FC<AppSidebarButtonProps> = ({
  show = true,
  onClick,
  subtitle,
  title,
  icon,
  color = 'bg-blue-500',
  isActive = false,
}) => {
  const { sidebarCollapsed } = useLayout();

  if (!show) {
    return null;
  }

  return (
    <div
      className={`flex gap-3 p-3 rounded-3xl cursor-pointer transition-all duration-200 ${
        isActive ? 'bg-gray-300' : 'hover:bg-gray-200'
      } ${sidebarCollapsed ? 'justify-center' : ''}`}
      onClick={onClick}
      title={sidebarCollapsed ? title : undefined}
    >
      <div className={`flex shrink-0 size-8 ${color} rounded-full items-center justify-center self-center`}>
        {icon}
      </div>
      {!sidebarCollapsed && (
        <div className="min-w-0 flex-1">
          <p className="font-bold text-sm leading-tight">
            {title}
          </p>
          <p className="text-xs text-gray-600 leading-tight">
            {subtitle}
          </p>
        </div>
      )}
    </div>
  );
};

interface AppSidebarProps {
  readonly activePage?: string;
}

const AppSidebarDesktop: React.FC<AppSidebarProps> = ({ activePage }) => {
  const userPermissions = useSelector((state: any) => state.userState.permissions);
  const navigate = useNavigate();
  const { sidebarCollapsed, toggleSidebar } = useLayout();

  const sidebarButtons: any[] = getSidebarButtons(userPermissions);

  const navTo = (path: string) => {
    navigate(path);
  };

  return (
    <div
      className={`fixed left-0 top-0 h-full bg-sidebar border-r border-gray-200 transition-all duration-300 z-40 ${
        sidebarCollapsed ? 'w-16' : 'w-80'
      }`}
    >
      {/* Header */}
      <div
        className={`flex items-center border-b border-gray-200 h-16 ${
          sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'
        }`}
      >
        <Button
          className="size-8 p-0 shrink-0"
          onClick={partial(navTo, '/dashboard')}
        >
          <NoNameLogo className="h-[18px] fill-white" />
        </Button>
        {!sidebarCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8"
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Content */}
      <div
        className={`flex flex-col gap-3 p-3 h-[calc(100%-4rem)] overflow-y-auto ${
          sidebarCollapsed ? 'items-center' : ''
        }`}
      >
        {sidebarButtons.map((button) => (
          <AppSidebarButton
            show={button.permissions.length > 0 ? button.permissions.includes(userPermissions) : true}
            key={button.title}
            title={button.title}
            subtitle={button.subtitle}
            color={button.color}
            icon={button.icon}
            onClick={partial(navTo, button.path)}
            isActive={activePage === button.path}
          />
        ))}

        <div className="mt-auto pt-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8"
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
        </div>

      </div>
    </div>
  );
};

const AppSidebarMobile: React.FC<AppSidebarProps> = ({ activePage }) => {
  const navigate = useNavigate();
  const { mobileSidebarOpen, setMobileSidebarOpen } = useLayout();

  const userPermissions = useSelector((state: any) => state.userState.permissions);
  const sidebarButtons: any[] = getSidebarButtons(userPermissions);

  const navTo = (path: string) => {
    navigate(path);
    setMobileSidebarOpen(false); // Close sidebar after navigation
  };

  return (
    <Sheet
      open={mobileSidebarOpen}
      onOpenChange={setMobileSidebarOpen}
    >
      <SheetTitle className="hidden" />
      <SheetContent
        side="left"
        className="w-80 p-0 bg-sidebar [&>button]:hidden"
        aria-describedby={undefined}
      >
        <div className="flex h-full w-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 h-16 px-4">
            <Button
              className="size-8 p-0"
              onClick={partial(navTo, '/dashboard')}
            >
              <NoNameLogo className="h-[18px] fill-white" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileSidebarOpen(false)}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-3 p-4 flex-1 overflow-y-auto">

            {sidebarButtons.map((button) => (
              <AppSidebarButton
                show={button.permissions.length > 0 ? button.permissions.includes(userPermissions) : true}
                key={button.title}
                title={button.title}
                subtitle={button.subtitle}
                color={button.color}
                icon={button.icon}
                onClick={partial(navTo, button.path)}
                isActive={activePage === button.path}
              />
            ))}

          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const AppSidebar: React.FC<AppSidebarProps> = ({ activePage }) => {
  const { isMobile } = useLayout();

  return (
    <>
      {isMobile ? (
        <AppSidebarMobile activePage={activePage} />
      ) : (
        <AppSidebarDesktop activePage={activePage} />
      )}
    </>
  );
};